import {useEffect, useState} from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useTheme } from '@mui/material/styles';

import Container from '../../components/Container';
import {Typography, Divider, Grid2 as Grid, Button, useMediaQuery, Link} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import { ReportTypeEnums } from '../../types/referenceData/reportTypeEnums';
import DepositReport from './reportTypes/DepositReport';
import reportLog from '../../types/reports/reportLog';
import CreditCardReport from './reportTypes/CreditCardReport';
import BankReport from './reportTypes/BankReport';
import BaseReportContent from '../../types/reports/baseReportContent';
import ReimbursementReport from './reportTypes/ReimbursementReport';
import PeriodicReport from './reportTypes/PeriodicReport';
import SubvendorReport from './reportTypes/SubvendorReport';
import PayrollItemizationReport from './reportTypes/PayrollItemizationReport';
import IEPACReport from './reportTypes/IEPACReport';
import DonationReport from './reportTypes/DonationReport';
import DepositoryD102Report from './reportTypes/DepositoryD102Report';
import LateContributionReport from './reportTypes/LateContributionReport';
import IEPACYearEndReport from './reportTypes/IEPACYearEndReport';
import IndependentExpendituresReport from './reportTypes/IndependentExpendituresReport';
import Cpf22Report from './reportTypes/Cpf22Report';
import ExternalActivityReport from './reportTypes/ExternalActivityReport';
import BankExpenditureItemizationReport from './reportTypes/BankExpenditureItemizationReport';

interface ReportProps {
    report?: reportLog;
    reportId?: number;
    isIE?: boolean;
}

const DisplayReport = (reportProps:ReportProps): JSX.Element => {
    
    document.title = 'OCPF - Display Report';

    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));
    const { showBoundary } = useErrorBoundary();  

    const [showHeader, setHeaderVisibility] = useState(false);
    const [reportTypeId, setReportTypeId] = useState(0);
    const [reportType, setReportType] = useState('');
    const [reportId, setReportId] = useState(0);
    const [selectedReport, setSelectedReport] = useState<BaseReportContent>();
    const [currentReport, setCurrentReport] = useState<any>();
    const [pdfTypeLink, setPdfTypeLink] = useState<string>('report');


    useEffect(() => {
        //open from report log
        if(reportProps.report){
            setReportTypeId(reportProps.report.reportTypeId);
            setReportType(reportProps.report.reportTypeId.toString());
            setReportId(reportProps.report.reportId);
            return;
        }
    
        //open from the url link with report id
        if(reportProps.reportId){
            getFullReport();
        }
    },[reportProps])


    const getFullReport = async () => {

        let apiCall = reportProps.isIE?'miscreport':'report';
        setPdfTypeLink(reportProps.isIE?'miscreport':'report');

        try{
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/${apiCall}/${reportProps.reportId}`)
            .then(result => {
            if (!result.ok) {
                showBoundary(new Error('Failed to get the report content.'));
            }
            return result.json();
        })
        .then(result => {

            let report = result;
            setHeaderVisibility(true);
            setSelectedReport(report);
            setCurrentReport(report);
            setReportId(report.reportId);
            if(reportProps.isIE){
                setReportType(report.reportType);
                setPdfTypeLink('miscreport');
            }
            else{
                setReportTypeId(report.reportTypeId);
                setPdfTypeLink('report');
            }
            
            return;
        });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
    }

    const renderReportContent = () => {
        switch(reportTypeId){
            case ReportTypeEnums.DepositReport:
                return <DepositReport currentReport={currentReport} reportId={reportId}></DepositReport>;
            case ReportTypeEnums.CreditCardReport:
                return <CreditCardReport currentReport={currentReport} reportId={reportId}></CreditCardReport>;
            case ReportTypeEnums.BankReportFirstHalf:
            case ReportTypeEnums.BankReportSecondHalf:
            case ReportTypeEnums.BankReport:
                return <BankReport reportId={reportId} currentReport={currentReport}></BankReport>;
            case ReportTypeEnums.ReimbursementReport:
                return <ReimbursementReport reportId={reportId} currentReport={currentReport}></ReimbursementReport>;
            case ReportTypeEnums.SubvendorReport:
                return <SubvendorReport reportId={reportId} currentReport={currentReport}></SubvendorReport>;
            case ReportTypeEnums.PayrollItemizationReport:
                return <PayrollItemizationReport reportId={reportId}></PayrollItemizationReport>;
            case ReportTypeEnums.BankExpenditureItemizationReport:
                return <BankExpenditureItemizationReport reportId={reportId} currentReport={currentReport}></BankExpenditureItemizationReport>;
            case ReportTypeEnums.IEPACReport:
                return <IEPACReport reportId={reportId} currentReport={currentReport}></IEPACReport>;
            case ReportTypeEnums.DonationReport:
                return <DonationReport reportId={reportId} currentReport={currentReport}></DonationReport>;
            case ReportTypeEnums.LateContributionReport:
                return <LateContributionReport reportId={reportId} currentReport={currentReport}></LateContributionReport>;
            case ReportTypeEnums.DepositoryYearEndReport:
                return <DepositoryD102Report reportId={reportId} currentReport={currentReport}></DepositoryD102Report>;
            case ReportTypeEnums.IEPACYearEndReport:
                return <IEPACYearEndReport reportId={reportId} currentReport={currentReport}></IEPACYearEndReport>;
            case ReportTypeEnums.ExternalActivityReport:
                return <ExternalActivityReport reportId={reportId} currentReport={currentReport}></ExternalActivityReport>;
            case ReportTypeEnums.DepositoryInitialReport:
            case ReportTypeEnums.DissolutionReport:
            case ReportTypeEnums.IEPACDissolutionReport:
            case ReportTypeEnums.LpcPrePrimary:
            case ReportTypeEnums.LpcYearEnd:
            case ReportTypeEnums.LpcPreElection:
            case ReportTypeEnums.BallotQuestionInitialReport:
            case ReportTypeEnums.BallotQuestionYearEnd:
            case ReportTypeEnums.BallotQuestionDissolutionReport:
            case ReportTypeEnums.BallotQuestion60thDayReport:
            case ReportTypeEnums.BallotQuestionSep20Report:
            case ReportTypeEnums.BallotQuestionOct5Report:
            case ReportTypeEnums.BallotQuestionOct20Report:
            case ReportTypeEnums.BallotQuestionNov5Report:
            case ReportTypeEnums.BallotQuestionNov20Report:
            case ReportTypeEnums.TransitionOutReport:
            case ReportTypeEnums.TrainsitionInReport:
            case ReportTypeEnums.MunPrePrimaryReport:
            case ReportTypeEnums.MunPreElectionReport:
            case ReportTypeEnums.MunYearEndReport:
                return <PeriodicReport reportId={reportId} currentReport={currentReport}></PeriodicReport>
            case (reportTypeId < 36 && reportTypeId >19 ? reportTypeId : -1):
                return <PeriodicReport reportId={reportId} currentReport={currentReport}></PeriodicReport>
            default:
                return <></>;
        }
    }

    const renderIEReportContent = () => {
        switch(reportType){
            case ReportTypeEnums.IEReport:
            case ReportTypeEnums.IndependentExpendituresReport:
            case ReportTypeEnums.ECReport:
            case ReportTypeEnums.ElectioneeringCommunicationsReport:
            case ReportTypeEnums.PoliticalSpendingReport:
                return <IndependentExpendituresReport reportId={reportId} currentReport={currentReport}></IndependentExpendituresReport>;
            case ReportTypeEnums.Cpf22sReport:
            case ReportTypeEnums.IndependentBallotQuestionSpending:
                return <Cpf22Report reportId={reportId} currentReport={currentReport}></Cpf22Report>;
            default:
                return <></>;
        }
    }

    return (
        <Container marginTop={-5}>
            {
                showHeader && selectedReport && (
                    <>
                    <Grid container>
                        <Grid size={{ xs: 12, md: 6 }} display={"flex"} justifyContent={"flex-start"}> 
                            <Typography variant='h6' fontWeight={550}>
                                {selectedReport.reportTypeDescription} {selectedReport.reportingPeriod}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }} display={"flex"} justifyContent={isSM?"flex-start":"flex-end"}>
                            <Button component={Link} href={`${process.env.REACT_APP_API_BASE_URL}/${pdfTypeLink}/pdf/${selectedReport?.reportId}`} target='_blank' underline="none" variant="text" startIcon={<PictureAsPdfIcon />} color="success">Export to PDF</Button>
                        </Grid>
                    </Grid>
                    <Divider textAlign="right">
                        <Typography variant='body2'>{selectedReport?.reportId}</Typography>
                    </Divider>
                    </>
                   
                )
            }
                {reportProps.isIE?renderIEReportContent():renderReportContent()}
        </Container>
    );
};

export default DisplayReport;